/**
 * Capture primitives — the shapes the widget collects and ships inside a
 * CreateFeedback payload, and that the dashboard renders back. These mirror the
 * JSON columns on `FeedbackSession` (transcript / actions / consoleLogs / errors)
 * and the viewport / page metadata.
 *
 * Kept deliberately permissive on the high-volume arrays (e.g. console args) so
 * the widget never fails ingestion over an odd serialized value, while still
 * pinning the fields the backend filters/renders on.
 */
import type { z } from 'zod';
import { array, boolean, number, object, record, string, unknown, url } from 'zod';

import { ConsoleLevelSchema, TranscriptRoleSchema } from './enums.js';

/** Browser viewport at capture time. */
export const ViewportSchema = object({
  width: number().int().nonnegative(),
  height: number().int().nonnegative(),
  /** devicePixelRatio. */
  dpr: number().positive().default(1),
});
export type Viewport = z.infer<typeof ViewportSchema>;

/** Page-level metadata captured alongside feedback. */
export const PageMetadataSchema = object({
  url: url(),
  title: string().max(1024).optional(),
  /** BCP-47 language tag of the page / user. */
  lang: string().max(35).optional(),
  userAgent: string().max(2048).optional(),
  referrer: string().max(2048).optional(),
  viewport: ViewportSchema.optional(),
});
export type PageMetadata = z.infer<typeof PageMetadataSchema>;

/**
 * A single user-action timeline entry (click / input / navigation / scroll …).
 * `target` is a stabilized selector path (see capture-core selector paths).
 */
export const UserActionSchema = object({
  /** Action kind — open-ended string so capture-core can add kinds freely. */
  type: string().min(1).max(64),
  /** Epoch milliseconds. */
  ts: number().int().nonnegative(),
  /** Selector path of the action target, if any. */
  target: string().max(2048).optional(),
  /** Redacted/short value for inputs, human-readable label, etc. */
  value: string().max(2048).optional(),
  /** Free-form extra context (already redacted client-side). */
  meta: record(string(), unknown()).optional(),
});
export type UserAction = z.infer<typeof UserActionSchema>;

/** A captured console entry. `args` are pre-serialized to strings client-side. */
export const ConsoleLogSchema = object({
  level: ConsoleLevelSchema,
  /** Epoch milliseconds. */
  ts: number().int().nonnegative(),
  /** Stringified console arguments. */
  args: array(string().max(8192)).max(64),
});
export type ConsoleLog = z.infer<typeof ConsoleLogSchema>;

/** A captured JS error. */
export const CapturedErrorSchema = object({
  message: string().max(8192),
  /** Epoch milliseconds. */
  ts: number().int().nonnegative(),
  stack: string().max(16384).optional(),
  source: string().max(2048).optional(),
  line: number().int().nonnegative().optional(),
  col: number().int().nonnegative().optional(),
  /** "error" | "unhandledrejection" | … (open-ended). */
  kind: string().max(64).optional(),
});
export type CapturedError = z.infer<typeof CapturedErrorSchema>;

/** A single conversation turn (text or voice transcript). */
export const TranscriptTurnSchema = object({
  role: TranscriptRoleSchema,
  text: string().max(16384),
  /** Epoch milliseconds. */
  ts: number().int().nonnegative(),
  /** True while the turn is still being streamed (partial transcription). */
  partial: boolean().optional(),
});
export type TranscriptTurn = z.infer<typeof TranscriptTurnSchema>;

/**
 * The full diagnostics bundle the widget attaches to feedback. Each array is
 * optional and size-capped; the server additionally enforces per-instance
 * capture-level limits.
 */
export const DiagnosticsSchema = object({
  actions: array(UserActionSchema).max(500).optional(),
  consoleLogs: array(ConsoleLogSchema).max(500).optional(),
  errors: array(CapturedErrorSchema).max(200).optional(),
});
export type Diagnostics = z.infer<typeof DiagnosticsSchema>;
